/**
 * Exportar para Excel: as tarefas que o quadro está mostrando (mesmo filtro e busca), com todas as concluídas,
 * em três abas: Tarefas, Carga por pessoa e Subtarefas. O arquivo é montado no navegador; nada passa pelo servidor.
 */
import { COLUMNS, PRIORITIES, type Task, type TaskStatus, type WorkspaceSettings, localToday, statusName } from '@quadro/domain';

interface Ctx {
  settings: WorkspaceSettings;
  person: (id: string) => { name: string };
  members: { id: string; name: string }[];
  entityName: (id: string) => string;
  routineName: (id: string) => string | undefined;
}

const date = (iso: string | null | undefined) => (iso ? new Date(`${iso.slice(0, 10)}T12:00:00`) : undefined);
const days = (from: string | null, to: string | null) => (from && to ? Math.max(0, Math.round((Date.parse(to) - Date.parse(from)) / 864e5)) : undefined);
const head = (value: string) => ({ value, fontWeight: 'bold' as const, backgroundColor: '#F2F2F2' });
const DATE = 'dd/mm/yyyy';

export async function exportTasksXlsx(tasks: Task[], ctx: Ctx, title: string) {
  const { default: writeXlsxFile } = await import('write-excel-file/browser');
  const today = localToday();
  const s = ctx.settings;
  const name = (id: string | null) => (id ? ctx.person(id).name : '');
  const project = (id: string | null) => s.projects.find(p => p.id === id)?.name ?? '';
  const category = (t: Task) => s.categories.find(c => c.id === t.classification?.categoryId);
  const order = new Map(COLUMNS.map((c, i) => [c.id, i]));
  const list = [...tasks].sort((a, b) => (order.get(a.status)! - order.get(b.status)!) || a.position - b.position);

  const situacao = (t: Task) => (t.status === 'done' ? 'Concluída' : !t.due ? 'Sem prazo' : t.due < today ? 'Atrasada' : t.due === today ? 'Vence hoje' : 'No prazo');
  const taskCols: { header: string; width: number; cell: (t: Task) => Record<string, unknown> }[] = [
    { header: 'Título', width: 44, cell: t => ({ value: t.title }) },
    { header: 'Etapa', width: 18, cell: t => ({ value: statusName(t.status) }) },
    { header: 'Situação', width: 12, cell: t => ({ value: situacao(t), color: situacao(t) === 'Atrasada' ? '#C00000' : undefined }) },
    { header: 'Projeto', width: 16, cell: t => ({ value: project(t.projectId) }) },
    { header: 'Responsável', width: 20, cell: t => ({ value: name(t.assigneeId) }) },
    { header: 'Prioridade', width: 11, cell: t => ({ value: PRIORITIES[t.priority] }) },
    { header: 'Prazo', width: 12, cell: t => ({ value: date(t.due), type: Date, format: DATE }) },
    { header: 'Aguardando', width: 18, cell: t => ({ value: t.waiting?.who ?? '' }) },
    { header: 'Cobrar em', width: 12, cell: t => ({ value: date(t.waiting?.followUpOn), type: Date, format: DATE }) },
    { header: 'Temas', width: 18, cell: t => ({ value: t.themeIds.map(id => s.themes.find(x => x.id === id)?.name).filter(Boolean).join(', ') }) },
    { header: 'Categoria', width: 16, cell: t => ({ value: category(t)?.name ?? '' }) },
    { header: 'Subcategoria', width: 16, cell: t => ({ value: category(t)?.subcategories.find(x => x.id === t.classification?.subcategoryId)?.name ?? '' }) },
    { header: 'Entidades', width: 22, cell: t => ({ value: t.entityIds.map(ctx.entityName).join(', ') }) },
    { header: 'Subtarefas', width: 10, cell: t => ({ value: t.subtasks.length ? `${t.subtasks.filter(x => x.done).length}/${t.subtasks.length}` : '' }) },
    { header: 'Rotina', width: 18, cell: t => ({ value: t.routineId ? ctx.routineName(t.routineId) ?? 'Rotina' : '' }) },
    { header: 'Links', width: 30, cell: t => ({ value: t.links.map(l => `${l.title}: ${l.url}`).join('\n'), wrap: true }) },
    { header: 'Criada em', width: 12, cell: t => ({ value: date(t.createdAt), type: Date, format: DATE }) },
    { header: 'Iniciada em', width: 12, cell: t => ({ value: date(t.startedAt), type: Date, format: DATE }) },
    { header: 'Concluída em', width: 12, cell: t => ({ value: date(t.completedAt), type: Date, format: DATE }) },
    { header: 'Ciclo (dias)', width: 10, cell: t => ({ value: days(t.startedAt, t.completedAt), type: Number }) },
    { header: 'Dias na etapa', width: 10, cell: t => ({ value: t.status === 'done' ? undefined : days(t.enteredStatusAt, new Date().toISOString()), type: Number }) },
    { header: 'Descrição', width: 50, cell: t => ({ value: t.description, wrap: true }) },
  ];

  // Carga por pessoa: abertas por etapa, atrasadas e entregas dos últimos 30 dias (inclui "Ninguém").
  const since = new Date(Date.now() - 30 * 864e5).toISOString();
  const people = [...ctx.members.map(m => ({ id: m.id as string | null, name: m.name })), { id: null, name: 'Ninguém' }];
  const open: TaskStatus[] = ['mapped', 'todo', 'doing', 'waiting'];
  const load = people.map(p => {
    const mine = list.filter(t => t.assigneeId === p.id);
    const o = mine.filter(t => t.status !== 'done');
    return [p.name, o.length, ...open.map(st => o.filter(t => t.status === st).length), o.filter(t => t.due && t.due < today).length,
      mine.filter(t => t.status === 'done' && (t.completedAt ?? '') >= since).length];
  }).filter(r => (r.slice(1) as number[]).some(n => n > 0));

  const subs = list.flatMap(t => t.subtasks.map(st => [
    { value: t.title }, { value: st.text }, { value: st.done ? 'Sim' : 'Não' }, { value: name(st.assigneeId) },
    { value: date(st.due), type: Date, format: DATE },
  ]));

  const file = writeXlsxFile([
    {
      sheet: 'Tarefas',
      data: [taskCols.map(c => head(c.header)), ...list.map(t => taskCols.map(c => c.cell(t)))],
      columns: taskCols.map(c => ({ width: c.width })),
      stickyRowsCount: 1,
    },
    {
      sheet: 'Carga por pessoa',
      data: [['Pessoa', 'Abertas', ...open.map(statusName), 'Atrasadas', 'Concluídas (30 dias)'].map(head), ...load.map(r => r.map(value => ({ value })))],
      columns: [{ width: 24 }, ...Array.from({ length: 7 }, () => ({ width: 14 }))],
      stickyRowsCount: 1,
    },
    {
      sheet: 'Subtarefas',
      data: [['Tarefa', 'Subtarefa', 'Feita', 'Responsável', 'Prazo'].map(head), ...subs],
      columns: [{ width: 40 }, { width: 40 }, { width: 8 }, { width: 20 }, { width: 12 }],
      stickyRowsCount: 1,
    },
  ] as never, { fontFamily: 'Calibri', fontSize: 11 } as never);
  const slug = title.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^A-Za-z0-9]+/g, '-').replace(/^-|-$/g, '').toLowerCase();
  await (file as unknown as { toFile: (n: string) => Promise<void> }).toFile(`quadro-${slug || 'tarefas'}-${today}.xlsx`);
  return list.length;
}
